// utils/payeeEntity.js
//
// The PAYEE's own entity type — the supplier / withholding agent on a row.
//
// Deliberately separate from utils/sawtFormType.js, which answers a different
// question about a different party: that one classifies the FILER (our client)
// to pick the BIR FORM 1701/1702 header and the DAT FTYPE_CODE. This one
// classifies each row's counterparty to decide which NAME COLUMN their name
// belongs in. Conflating the two puts a supplier's registered name under the
// wrong heading, which is a data error, not a cosmetic one.
//
// Source field is `Vendor.businessType` (backend/models/Vendor.js:56), enum
// ['individual', 'corporation', 'partnership', 'government']. NOT `Vendor.type`
// — no such field exists, and reading it returned undefined every time, which
// silently classified every payor as an individual.

// Everything that is not a natural person files under its registered name.
// 'opc' is accepted too: it is not in Vendor's enum, but AccountingClient uses
// it and a vendor record could be seeded from one.
const CORPORATE_PAYEE_TYPES = new Set(['corporation', 'partnership', 'government', 'opc']);

/**
 * Resolve a payee's entity type.
 * @returns {'corporation'|'individual'|null} null when unset or unrecognized —
 *   callers must treat that as a data gap, never as a default.
 */
export function resolvePayeeEntity(businessType) {
  const key = String(businessType || '').trim().toLowerCase();
  if (!key) return null;
  if (key === 'individual') return 'individual';
  if (CORPORATE_PAYEE_TYPES.has(key)) return 'corporation';
  return null;
}

/**
 * Boolean form for call sites that only branch two ways (e.g. the DAT export's
 * name-field layout). An unresolvable type reads as false — the DAT format has
 * no third state. Use resolvePayeeEntity where a gap must be surfaced.
 */
export function isCorporatePayee(businessType) {
  return resolvePayeeEntity(businessType) === 'corporation';
}

/**
 * Names the payees whose entity type cannot be resolved.
 * @param {Array<{name?: string, businessType?: unknown}>} payees — in row order
 * @returns {string[]} display labels, deduped, row order preserved
 */
export function findMissingPayeeEntities(payees) {
  const seen = new Set();
  const missing = [];
  payees.forEach(({ name, businessType }, i) => {
    if (resolvePayeeEntity(businessType)) return;
    const label = String(name || '').trim() || `(unnamed payee, row ${i + 1})`;
    if (seen.has(label)) return;
    seen.add(label);
    missing.push(label);
  });
  return missing;
}

/** Human-readable detail for a blocked export. */
export function missingPayeeEntityMessage(missing, vendorTerm = 'vendor') {
  const shown = missing.slice(0, 10);
  const rest = missing.length - shown.length;
  const who = shown.join(', ') + (rest > 0 ? `, and ${rest} more` : '');
  return `Cannot tell whether ${missing.length} payee${missing.length === 1 ? ' is' : 's are'} `
    + `a corporation or an individual: ${who}. Set Business Type on each ${vendorTerm} record — `
    + 'guessing would file their name under the wrong column.';
}
